import { useCallback, useState } from "react";
import { Capacitor } from "@capacitor/core";

/**
 * One-shot coarse location for "Stations near me". Only offered where the
 * browser's geolocation works without extra native setup — the Capacitor
 * apps don't declare a location permission, so it's web-only for now.
 * The fix is cached for the session; we never watch position.
 */
type Coords = { lat: number; lng: number };
const CACHE_KEY = "orvis:geo";

function readCache(): Coords | undefined {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Coords) : undefined;
  } catch {
    return undefined;
  }
}

export function geolocationSupported(): boolean {
  return typeof navigator !== "undefined" && "geolocation" in navigator && !Capacitor.isNativePlatform();
}

export function useGeolocation() {
  const [coords, setCoords] = useState<Coords | undefined>(() => readCache());
  const [status, setStatus] = useState<"idle" | "locating" | "denied" | "error">("idle");

  const request = useCallback((): Promise<Coords | undefined> => {
    if (coords) return Promise.resolve(coords);
    if (!geolocationSupported()) return Promise.resolve(undefined);
    setStatus("locating");
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCoords(c);
          setStatus("idle");
          try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(c));
          } catch { /* ignore */ }
          resolve(c);
        },
        (err) => {
          setStatus(err.code === err.PERMISSION_DENIED ? "denied" : "error");
          resolve(undefined);
        },
        { enableHighAccuracy: false, timeout: 10_000, maximumAge: 30 * 60_000 }
      );
    });
  }, [coords]);

  return { coords, status, request };
}
